import React, { useState } from 'react';
import { Dialog, DialogContent, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { useNavigate } from 'react-router-dom';
import {
  Users, FolderOpen, Lightning, Sparkle, ArrowRight, ArrowLeft, CheckCircle, X,
  ClipboardText, Kanban, Cards, ListBullets, Clock, Eye, FileText, PencilSimple, PlugsConnected,
} from '@phosphor-icons/react';

// Multi-step onboarding tour that walks a new admin through the essential setup
// of an Inspiro Projects workspace. Triggered from Settings → "Replay Setup Tour".
// Each step is self-contained (no real navigation until the final action button),
// so users can flip back and forth without losing state.

const STEPS = [
  {
    id: 'invite',
    icon: Users,
    color: 'from-blue-500 to-indigo-600',
    title: 'Invite your teammates',
    subtitle: 'People',
    body: (
      <>
        <p>Every workspace starts with people. Head to <strong>Members</strong> and click <em>Invite Member</em>. Assign a role:</p>
        <ul className="list-disc pl-5 mt-2 space-y-1 text-[13px]">
          <li><strong>Admin</strong> — full workspace control</li>
          <li><strong>Project Manager</strong> — can create projects and invite contributors</li>
          <li><strong>Contributor</strong> — can create and edit tasks on assigned projects</li>
          <li><strong>Viewer</strong> — read-only access</li>
        </ul>
        <p className="text-[12px] text-zinc-500 mt-3">Tip: Free plan allows up to 2 members. Upgrade to Business for unlimited seats.</p>
      </>
    ),
    cta: { label: 'Open Members', href: '/team' },
  },
  {
    id: 'project',
    icon: FolderOpen,
    color: 'from-emerald-500 to-teal-600',
    title: 'Create your first project',
    subtitle: 'Projects',
    body: (
      <>
        <p>Click <strong>+ New Project</strong> on the Projects page, or use the <kbd className="px-1.5 py-0.5 text-[10px] font-mono rounded bg-zinc-100 border border-zinc-200">+</kbd> button in the sidebar. Pick a template:</p>
        <ul className="list-disc pl-5 mt-2 space-y-1 text-[13px]">
          <li><strong>Scrum</strong> — sprints, story points, burndown</li>
          <li><strong>Kanban</strong> — continuous flow, WIP limits</li>
          <li><strong>Bug Tracker</strong> — severity, reproducibility fields</li>
          <li><strong>Blank</strong> — start from scratch</li>
        </ul>
        <p className="text-[12px] text-zinc-500 mt-3">Each project gets its own board, members, custom fields, and settings.</p>
      </>
    ),
    cta: { label: 'Open Projects', href: '/projects' },
  },
  {
    id: 'custom-fields',
    icon: ClipboardText,
    color: 'from-violet-500 to-purple-600',
    title: 'Add custom fields',
    subtitle: 'Fields',
    body: (
      <>
        <p>Go to <strong>Project → Settings → Custom fields</strong> and add fields that matter to your team:</p>
        <ul className="list-disc pl-5 mt-2 space-y-1 text-[13px]">
          <li><strong>Acceptance Criteria</strong> (rich text)</li>
          <li><strong>Severity</strong> (dropdown: S1 / S2 / S3)</li>
          <li><strong>Release</strong> (text or dropdown)</li>
          <li><strong>ETA</strong> (date)</li>
        </ul>
        <p className="text-[12px] text-zinc-500 mt-3">Custom fields show up on every task and are searchable / filterable.</p>
      </>
    ),
    cta: { label: 'Open Projects', href: '/projects' },
  },
  {
    id: 'card-layout',
    icon: Cards,
    color: 'from-rose-500 to-pink-600',
    title: 'Customize your card layout',
    subtitle: 'Card layout',
    body: (
      <>
        <p>Decide which fields show up on the Kanban card. Go to <strong>Project → Settings → Card layout</strong> and toggle up to 7 fields, including any custom field you've created.</p>
        <ul className="list-disc pl-5 mt-2 space-y-1 text-[13px]">
          <li>Standard fields — Ticket Key, Due Date, Priority, Story Points, Assignee, Tags, Subtasks, Reporter, Sprint, Severity, Time Spent, Original Estimate, Created, Updated</li>
          <li>Custom fields — auto-discovered from your project (excluding rich-text fields)</li>
        </ul>
        <p className="text-[12px] text-zinc-500 mt-3">Tip: Less is more. Pick the 4–6 fields your team scans for daily and the board stays uncluttered.</p>
      </>
    ),
    cta: { label: 'Open Projects', href: '/projects' },
  },
  {
    id: 'story-points',
    icon: Lightning,
    color: 'from-amber-500 to-orange-500',
    title: 'Estimate with story points',
    subtitle: 'Estimation',
    body: (
      <>
        <p>On any task, set <strong>Story Points</strong> in the task details panel. We use the Fibonacci scale:</p>
        <div className="flex flex-wrap gap-1.5 mt-2">
          {['1', '2', '3', '5', '8', '13', '21'].map(n => (
            <span key={n} className="px-2 py-1 rounded-md text-[12px] font-bold tabular-nums bg-indigo-50 text-indigo-700 border border-indigo-100">{n} SP</span>
          ))}
        </div>
        <p className="text-[13px] mt-3">When a sprint runs, the <strong>Insights → Velocity</strong> chart shows completed vs committed points per sprint — your team's working capacity.</p>
        <p className="text-[12px] text-zinc-500 mt-2">Tip: 1 SP ≈ a couple of hours. 13+ SP usually means "too big, break it down".</p>
      </>
    ),
    cta: { label: 'Open Insights', href: '/insights' },
  },
  {
    id: 'board',
    icon: Kanban,
    color: 'from-fuchsia-500 to-pink-600',
    title: 'Use the Kanban board',
    subtitle: 'Board',
    body: (
      <>
        <p>Inside any project, click the <strong>Board</strong> tab. You can:</p>
        <ul className="list-disc pl-5 mt-2 space-y-1 text-[13px]">
          <li>Drag cards between columns (To Do → In Progress → Review → Done)</li>
          <li>Click <strong>Create Item</strong> in the header for a new task</li>
          <li>Filter by assignee, priority, type, or saved filter</li>
          <li>Reorder columns by dragging headers — order persists across reloads</li>
        </ul>
        <p className="text-[12px] text-zinc-500 mt-3">Cards respect dependency rules — you'll get a warning before moving a blocked task to Done.</p>
      </>
    ),
    cta: { label: 'Open Projects', href: '/projects' },
  },
  {
    id: 'backlog',
    icon: ListBullets,
    color: 'from-sky-500 to-blue-600',
    title: 'Plan with Backlog & Sprints',
    subtitle: 'Backlog',
    body: (
      <>
        <p>Open the <strong>Backlog</strong> tab inside a Scrum project to plan ahead:</p>
        <ul className="list-disc pl-5 mt-2 space-y-1 text-[13px]">
          <li>Create future sprints (start &amp; end dates) and drag tickets in</li>
          <li>The Board tab shows only the active sprint — backlog stays out of the way</li>
          <li>Start a sprint when you're ready — Insights begin tracking burndown automatically</li>
          <li>Complete a sprint to roll unfinished items back to the backlog</li>
        </ul>
        <p className="text-[12px] text-zinc-500 mt-3">Tip: Owners, Admins and Project Managers can manage sprints. Contributors can drag tasks in/out but can't start or close sprints.</p>
      </>
    ),
    cta: { label: 'Open Projects', href: '/projects' },
  },
  {
    id: 'time-tracking',
    icon: Clock,
    color: 'from-cyan-500 to-teal-600',
    title: 'Track time on each task',
    subtitle: 'Time tracking',
    body: (
      <>
        <p>Open any task to log effort. Three values work together:</p>
        <ul className="list-disc pl-5 mt-2 space-y-1 text-[13px]">
          <li><strong>Original estimate</strong> — set once at planning time. Auto-fills from <em>Story Points × hours-per-SP</em> if your project defines a ratio.</li>
          <li><strong>Time spent</strong> — log hours as you work. Each entry is appended to the task history.</li>
          <li><strong>Remaining</strong> — auto-recalculated as <em>estimate − spent</em>, or override manually if scope changes.</li>
        </ul>
        <p className="text-[12px] text-zinc-500 mt-3">Insights → Cycle Time shows how long tickets actually take vs your estimates so you can plan smarter next sprint.</p>
      </>
    ),
    cta: { label: 'Open Insights', href: '/insights' },
  },
  {
    id: 'watchers',
    icon: Eye,
    color: 'from-indigo-500 to-violet-600',
    title: 'Watch tasks you care about',
    subtitle: 'Watchers',
    body: (
      <>
        <p>Click the <strong>Watch</strong> toggle in any task's top bar to subscribe. You'll get notified when:</p>
        <ul className="list-disc pl-5 mt-2 space-y-1 text-[13px]">
          <li>Status, assignee, priority or due date changes</li>
          <li>Someone comments, reacts or @-mentions on the task</li>
          <li>A new attachment, link or subtask is added</li>
          <li>The task is moved to Done or reopened</li>
        </ul>
        <p className="text-[13px] mt-3">Your <strong>Watching</strong> list lives on the Home dashboard so you can scan everything you've subscribed to in one place.</p>
        <p className="text-[12px] text-zinc-500 mt-2">Tip: Reporters and assignees are added as watchers automatically.</p>
      </>
    ),
    cta: { label: 'Open Home', href: '/dashboard' },
  },
  {
    id: 'docs',
    icon: FileText,
    color: 'from-emerald-500 to-green-600',
    title: 'Document with Docs',
    subtitle: 'Docs',
    body: (
      <>
        <p>Every project has a <strong>Docs</strong> tab — a lightweight wiki for specs, runbooks, retros and onboarding notes.</p>
        <ul className="list-disc pl-5 mt-2 space-y-1 text-[13px]">
          <li>Rich-text editor with headings, lists, tables, code blocks &amp; image paste</li>
          <li>Inline @-mention teammates and link tasks like <code className="font-mono bg-zinc-100 px-1 rounded">MOB-123</code></li>
          <li>Pages are tenant-isolated and respect your project's role permissions</li>
        </ul>
        <p className="text-[12px] text-zinc-500 mt-3">Available on Business plan (15 pages/project). Upgrade to Premium for unlimited.</p>
      </>
    ),
    cta: { label: 'Open Projects', href: '/projects' },
  },
  {
    id: 'whiteboard',
    icon: PencilSimple,
    color: 'from-orange-500 to-rose-500',
    title: 'Brainstorm on a Whiteboard',
    subtitle: 'Whiteboard',
    body: (
      <>
        <p>Open the <strong>Whiteboard</strong> tab inside any project for an infinite canvas — perfect for architecture diagrams, user-flow maps and retros.</p>
        <ul className="list-disc pl-5 mt-2 space-y-1 text-[13px]">
          <li>7 node types — Process, Decision, Circle, Database, Cloud, Sticky note, Text</li>
          <li>Drag from any node edge to connect — smart connectors with arrow start / end markers</li>
          <li>7-color palette · snap-to-grid for clean layouts</li>
          <li>Multiple boards per project (5 on Business, unlimited on Premium)</li>
        </ul>
        <p className="text-[12px] text-zinc-500 mt-3">Tip: Use sticky notes for retro themes (Start / Stop / Continue) — quick and visual.</p>
      </>
    ),
    cta: { label: 'Open Projects', href: '/projects' },
  },
  {
    id: 'integrations',
    icon: PlugsConnected,
    color: 'from-violet-500 to-fuchsia-600',
    title: 'Connect Slack & Microsoft Teams',
    subtitle: 'Integrations',
    body: (
      <>
        <p>Wire up your team's chat tool from <strong>Settings → Integrations</strong>:</p>
        <ul className="list-disc pl-5 mt-2 space-y-1 text-[13px]">
          <li><strong>Slack</strong> — OAuth connect, slash commands, threaded replies on ticket updates</li>
          <li><strong>Microsoft Teams</strong> — OAuth connect, channel notifications on assignment / status change</li>
          <li>Pick which channel to deliver each project's updates to</li>
        </ul>
        <p className="text-[13px] mt-3">You're all set! Re-run this tour anytime from <strong>Settings → Replay Setup Tour</strong>.</p>
      </>
    ),
    cta: { label: 'Finish & go to Home', href: '/dashboard' },
  },
];

export default function SetupTourDialog({ open, onClose }) {
  const [idx, setIdx] = useState(0);
  const navigate = useNavigate();
  const step = STEPS[idx];
  const Icon = step.icon;
  const isLast = idx === STEPS.length - 1;
  const isFirst = idx === 0;

  const handleCtaClick = () => {
    onClose?.();
    navigate(step.cta.href);
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-xl p-0 overflow-hidden" data-testid="setup-tour-dialog">
        {/* Gradient hero strip with Sparkle badge */}
        <div className={`bg-gradient-to-br ${step.color} px-6 pt-6 pb-8 text-white relative overflow-hidden`}>
          <div className="absolute -right-6 -bottom-6 w-32 h-32 rounded-full bg-white/10 blur-2xl pointer-events-none" />
          <button
            onClick={onClose}
            className="absolute top-3 right-3 w-7 h-7 rounded-full bg-white/15 hover:bg-white/25 flex items-center justify-center transition-colors"
            data-testid="setup-tour-close"
            aria-label="Close"
          >
            <X size={14} weight="bold" />
          </button>
          <div className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-white/15 text-[10px] uppercase tracking-[0.15em] font-bold mb-3">
            <Sparkle size={10} weight="fill" /> Step {idx + 1} of {STEPS.length} · {step.subtitle}
          </div>
          <div className="flex items-start gap-3">
            <div className="w-11 h-11 rounded-xl bg-white/20 backdrop-blur flex items-center justify-center flex-shrink-0">
              <Icon size={22} weight="bold" />
            </div>
            <div>
              <DialogTitle className="font-['Outfit'] text-[22px] font-black tracking-tight leading-tight">
                {step.title}
              </DialogTitle>
            </div>
          </div>
        </div>

        {/* Body */}
        <div className="px-6 py-5 text-[14px] leading-relaxed text-zinc-700 space-y-2 max-h-[55vh] overflow-y-auto">
          {step.body}
        </div>

        {/* Step dots + navigation */}
        <div className="px-6 pb-5 flex items-center justify-between gap-3 border-t border-zinc-100 pt-4">
          <div className="flex items-center gap-1.5 flex-wrap" data-testid="setup-tour-dots">
            {STEPS.map((s, i) => (
              <button
                key={s.id}
                onClick={() => setIdx(i)}
                className={`rounded-full transition-all ${i === idx ? 'w-6 h-1.5 bg-indigo-600' : i < idx ? 'w-1.5 h-1.5 bg-indigo-300' : 'w-1.5 h-1.5 bg-zinc-200'}`}
                aria-label={`Go to step ${i + 1}`}
                data-testid={`setup-tour-dot-${i}`}
              />
            ))}
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setIdx(Math.max(0, idx - 1))}
              disabled={isFirst}
              className="text-[12px] text-zinc-600 disabled:opacity-30"
              data-testid="setup-tour-prev"
            >
              <ArrowLeft size={13} className="mr-1" /> Back
            </Button>
            {isLast ? (
              <Button
                size="sm"
                onClick={handleCtaClick}
                className="text-[12px] bg-gradient-to-r from-indigo-600 to-violet-600 hover:from-indigo-700 hover:to-violet-700 text-white shadow-sm"
                data-testid="setup-tour-finish"
              >
                <CheckCircle size={13} weight="bold" className="mr-1.5" /> Finish &amp; go to Home
              </Button>
            ) : (
              <Button
                size="sm"
                onClick={() => setIdx(idx + 1)}
                className="text-[12px] bg-indigo-600 hover:bg-indigo-700 text-white"
                data-testid="setup-tour-next"
              >
                Next <ArrowRight size={13} weight="bold" className="ml-1" />
              </Button>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
